// frontend/src/components/Patients/Patients.js

import React, { useEffect, useState } from 'react';
import '../../css/bootstrap.min.css';
import '../../css/feathericon.min.css';
import '../../css/font-awesome.min.css';
import '../../css/style.css';
import '../../plugins/datatables/datatables.min.css';
import axios from 'axios';
import AdminSidebar from '../Adminsidebar/AdminSidebar';
import '../../css/admin-sidebar.css';

function Patients() {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Store the full patient object for editing and just the ID for deleting
    const [currentPatient, setCurrentPatient] = useState(null);

    // FIX: State now matches the backend Mongoose schema for consistency
    const initialFormState = {
        firstName: '',
        lastName: '',
        age: '',
        address: '',
        phone: '',
        email: ''
    };
    const [formData, setFormData] = useState(initialFormState);
    
    // Use a consistent API base URL. For development, this is often localhost.
    const API_BASE_URL = process.env.API_BASE_URL || 'https://api.hitkarihospital.com:5005'; 

    // --- API Functions ---
    const fetchPatients = async () => {
        setLoading(true);
        setError(""); // Clear previous errors on new fetch
        try {
            // FIX: Use consistent API endpoint
            const response = await axios.get(`${API_BASE_URL}/patients`);
            setPatients(response.data);
        } catch (err) {
            setError("Failed to fetch patients. " + (err.response?.data?.message || err.message));
            console.error("Error fetching patients:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPatients();
    }, []);

    // Handle DataTables initialization
    useEffect(() => {
        if (!loading && patients.length > 0) {
            if (window.$ && window.$.fn.DataTable.isDataTable('.datatable')) {
                window.$('.datatable').DataTable().destroy();
            }
            setTimeout(() => {
                if (window.$ && window.$('.datatable').length > 0) {
                   window.$('.datatable').DataTable();
                }
            }, 100);
        }
    }, [patients, loading]);

    // --- Modal and Form Handling ---
    const handleCloseModal = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setShowDeleteModal(false);
        setFormData(initialFormState); // Reset form data
        setCurrentPatient(null); // Clear current patient
        setError(""); // Clear modal-specific errors
    };

    const handleAddPatient = () => {
        setFormData(initialFormState); // Ensure form is empty for new patient
        setShowAddModal(true);
    };

    const handleEditPatient = (patient) => {
        setCurrentPatient(patient); // Store the entire patient object
        // FIX: Populate form data directly from the patient object
        setFormData({
            firstName: patient.firstName || '',
            lastName: patient.lastName || '',
            age: patient.age || '',
            address: patient.address || '',
            phone: patient.phone || '',
            email: patient.email || '',
        });
        setShowEditModal(true);
    };

    const handleDeletePatient = (patient) => {
        setCurrentPatient(patient); // Store patient to show name in confirmation
        setShowDeleteModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // --- Form Submission Logic (CRUD) ---
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError(""); // Clear previous errors

        // Basic validation
        if (!formData.firstName || !formData.lastName || !formData.age || !formData.phone) {
            setError("First Name, Last Name, Age, and Phone are required.");
            return;
        }

        try {
            let response;
            if (showEditModal) {
                // --- UPDATE (PUT) LOGIC ---
                // FIX: Send request to the correct PUT endpoint with patientId
                response = await axios.put(`${API_BASE_URL}/patients/${currentPatient.patientId}`, formData);
            } else {
                // --- CREATE (POST) LOGIC ---
                // FIX: Send request to the correct POST endpoint
                response = await axios.post(`${API_BASE_URL}/patients`, formData);
            }

            if (response.status === 201 || response.status === 200) {
                fetchPatients(); // Refresh the patient list
                handleCloseModal(); // Close the modal on success
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || "An unexpected error occurred.";
            setError(errorMsg); // Display error in the modal
            console.error("Error submitting form:", err.response || err);
        }
    };

    const confirmDelete = async () => {
        if (!currentPatient?.patientId) return;
        try {
            // FIX: Send request to the correct DELETE endpoint with patientId
            await axios.delete(`${API_BASE_URL}/patients/${currentPatient.patientId}`);
            fetchPatients(); // Refresh the list
            handleCloseModal(); // Close the modal
        } catch (err) {
            const errorMsg = `Failed to delete patient: ${err.response?.data?.message || err.message}`;
            setError(errorMsg);
            console.error("Error deleting patient:", err.response || err);
        }
    };

    if (loading && patients.length === 0) return <p>Loading Patients...</p>;

    return (
        <div className="main-wrapper">
            <AdminSidebar/>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-7 col-auto">
                                <h3 className="page-title">List of Patients</h3>
                                {/* Breadcrumb etc. */}
                            </div>
                            <div className="col-sm-5 col">
                                <button onClick={handleAddPatient} className="btn btn-primary float-right mt-2">Add Patient</button>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        {loading && <p>Refreshing data...</p>}
                                        {!loading && patients.length === 0 && <p>No patients found. Click 'Add Patient' to begin.</p>}
                                        
                                        <table className="datatable table table-hover table-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Patient ID</th>
                                                    <th>Patient Name</th>
                                                    <th>Age</th>
                                                    <th>Address</th>
                                                    <th>Phone</th>
                                                    <th>Email</th>
                                                    <th className="text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {patients.map((patient) => (
                                                    <tr key={patient.patientId}>
                                                        <td>{patient.patientId}</td>
                                                        {/* FIX: Use firstName and lastName from the data */}
                                                        <td>{`${patient.firstName} ${patient.lastName}`}</td>
                                                        <td>{patient.age}</td>
                                                        <td>{patient.address}</td>
                                                        <td>{patient.phone}</td>
                                                        <td>{patient.email}</td>
                                                        <td className="text-right">
                                                            <div className="actions">
                                                                <button onClick={() => handleEditPatient(patient)} className="btn btn-sm bg-success-light mr-1">
                                                                    <i className="fe fe-pencil"></i> Edit
                                                                </button>
                                                                <button onClick={() => handleDeletePatient(patient)} className="btn btn-sm bg-danger-light">
                                                                    <i className="fe fe-trash"></i> Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {(showAddModal || showEditModal) && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content" style={{marginTop:'50px'}}>
                            <div className="modal-header">
                                <h5 className="modal-title">{showEditModal ? 'Edit Patient' : 'Add Patient'}</h5>
                                <button type="button" className="close" onClick={handleCloseModal}><span>×</span></button>
                            </div>
                            <div className="modal-body">
                                {error && <div className="alert alert-danger">{error}</div>}
                                <form onSubmit={handleFormSubmit}>
                                    {/* FIX: Form fields now match the backend schema (firstName, lastName, phone, etc.) */}
                                    <div className="form-group">
                                        <label>First Name <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Last Name <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Age <span className="text-danger">*</span></label>
                                        <input type="number" className="form-control" name="age" value={formData.age} onChange={handleInputChange} required min="0" />
                                    </div>
                                    <div className="form-group">
                                        <label>Phone Number <span className="text-danger">*</span></label>
                                        <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Address</label>
                                        <input type="text" className="form-control" name="address" value={formData.address} onChange={handleInputChange} />
                                    </div>
                                    <button type="submit" className="btn btn-primary btn-block">
                                        {showEditModal ? 'Update Patient' : 'Add Patient'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body text-center">
                                <h4 className="modal-title">Confirm Delete</h4>
                                <p className="my-3">Are you sure you want to delete patient "{currentPatient?.firstName} {currentPatient?.lastName}"?</p>
                                <button type="button" className="btn btn-danger mr-2" onClick={confirmDelete}>Confirm</button>
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Patients;